import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  shareReplay,
  switchMap,
} from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { JsonResponse } from '../../../../../shared/api/backend-config';

export type PoiMode = 'ori' | 'school';
export interface NearestPoi {
  azimuth: number;
  distanceMiles: number;
  coordinateLat: number;
  coordinateLon: number;
  policeStationName?: string;
  schoolName?: string;
  formattedName?: string;
}

@Injectable({ providedIn: 'any' }) // per-host instance to avoid cross-talk between ori/schools
export class NearestPoiService {
  private http = inject(HttpClient);

  private center$ = new BehaviorSubject<mapboxgl.LngLat | null>(null);
  private mode$ = new BehaviorSubject<PoiMode>('ori'); // default

  // Update center on moveend
  setCenter(center: mapboxgl.LngLat) {
    this.center$.next(center);
  }
  // Set current instance mode to handle ori/schools separately
  setMode(mode: PoiMode) {
    this.mode$.next(mode);
  }

  // Stream of poi updates
  readonly poi$: Observable<NearestPoi | null> = combineLatest([
    this.center$,
    this.mode$,
  ]).pipe(
    debounceTime(250),
    distinctUntilChanged(
      ([a, m1], [b, m2]) =>
        !!a && !!b && m1 === m2 && a.lat === b.lat && a.lng === b.lng
    ),
    switchMap(([center, mode]) => {
      if (!center) return of<NearestPoi | null>(null);

      const endpoint =
        mode === 'ori' ? 'features/crime/nearest' : 'pois/school/nearest';

      return this.http
        .get<JsonResponse<NearestPoi>>(environment.apiUrl + endpoint, {
          params: { lon: center.lng, lat: center.lat },
        })
        .pipe(
          map((r) => {
            const preformattedName =
              r.object.policeStationName ?? r.object.schoolName;
            let formattedName = '';

            if (preformattedName) {
              formattedName = preformattedName
                .toLowerCase()
                .replace(/\b\w/g, (c: any) => c.toUpperCase());
            }

            return { ...r.object, formattedName };
          })
        );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}
